/**
 * OwnerPhoto — John Stephens, owner of A1 Screening.
 *
 * Renders a responsive <picture> for the owner photo with an optional
 * caption underneath. The visible caption (when shown) reads:
 *   "John Stephens, Owner of A1 Screening"
 *
 * Wording rules (Feb 2026):
 *   - Alt text: "John Stephens, owner of A1 Screening"
 *   - Caption:  "John Stephens, Owner of A1 Screening"
 *   - Do NOT add "Founder", "Licensed contractor", "Master technician",
 *     "Best screen company", "#1 in Naples", "Same-day", "24/7",
 *     "Guaranteed quote", or "Instant quote".
 *
 * `variant` accepts:
 *   - "framed-card" (default): Hero-style card with cyan border + caption
 *     plate that anchors at the bottom. Matches the original placeholder
 *     used on the Home page.
 *   - "rounded": Plain rounded image with optional caption underneath.
 *     Used on the About + Contact pages.
 */
const BASE = "/images/owner/web/john-stephens-owner";

export default function OwnerPhoto({
    variant = "framed-card",
    aspectClass = "aspect-[4/5]",
    objectPositionClass = "object-top",
    showCaption = true,
    captionRole = "Owner of A1 Screening",
    captionName = "John Stephens",
    eager = false,
    className = "",
    testId = "owner-photo",
}) {
    const picture = (
        <picture>
            <source type="image/webp" srcSet={`${BASE}-md.webp 640w, ${BASE}-lg.webp 1280w`} sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw" />
            <source type="image/jpeg" srcSet={`${BASE}-md.jpg 640w, ${BASE}-lg.jpg 1280w`} sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw" />
            <img
                src={`${BASE}-md.jpg`}
                alt="John Stephens, owner of A1 Screening"
                width="576"
                height="1024"
                loading={eager ? "eager" : "lazy"}
                decoding="async"
                className={`absolute inset-0 w-full h-full ${objectPositionClass}`}
                style={{ objectFit: "cover" }}
            />
        </picture>
    );

    if (variant === "framed-card") {
        return (
            <div className={`relative ${className}`} data-testid={testId}>
                <div
                    className={`${aspectClass} rounded-lg border-2 overflow-hidden relative`}
                    style={{
                        borderColor: "var(--color-accent)",
                        background: "var(--color-bg-card)",
                    }}
                >
                    {picture}
                </div>
                {showCaption ? (
                    <div
                        className="absolute -bottom-4 left-4 right-4 px-5 py-3 rounded-md text-center"
                        style={{ background: "var(--color-bg-card)", border: "1px solid var(--color-line-2)" }}
                        data-testid={`${testId}-caption`}
                    >
                        <p className="serif text-base font-bold m-0" style={{ color: "var(--color-text)" }}>
                            {captionName}
                        </p>
                        <p className="text-[11px] uppercase tracking-[0.18em] font-bold m-0" style={{ color: "var(--color-accent)" }}>
                            {captionRole}
                        </p>
                    </div>
                ) : null}
            </div>
        );
    }

    // "rounded" — used on About + Contact pages
    return (
        <figure className={`m-0 ${className}`} data-testid={testId}>
            <div
                className={`${aspectClass} rounded-lg overflow-hidden relative`}
                style={{ background: "var(--color-bg-card)" }}
            >
                {picture}
            </div>
            {showCaption ? (
                <figcaption
                    className="text-sm text-center mt-2"
                    style={{ color: "var(--color-text-muted)" }}
                    data-testid={`${testId}-caption`}
                >
                    {captionName}, {captionRole}
                </figcaption>
            ) : null}
        </figure>
    );
}
